<template>
  <div class="icon">
    <Badge
      rounded
      :size="badgeSize"
      :content="badgeContent"
    >
      <!-- Keep severity-based color:  -->
      <div :style="'color: var(--fg-' + severity + ')!important'">
        <!-- Only show "featured" circles if using Font Awesome -->
        <i
          v-if="featured && library === 'fa'"
          class="far fa-circle absolute mt-[3px] scale-[1.8] opacity-15"
        />
        <i
          v-if="featured && library === 'fa'"
          class="fal fa-circle absolute mt-[3px] scale-[2.8] opacity-5"
        />

        <!-- If we're using Font Awesome: -->
        <i
          v-if="library === 'fa'"
          :class="['fa-' + animation, faStyle, 'fa-' + iconName]"
        ></i>

        <!-- If we're using Material Icons: -->
        <i
          v-else-if="library === 'material'"
          class="material-symbols-outlined"
          :style="{ transform: 'scale(1.3)', 'margin-top': '0.3rem' }"
        >
          {{ iconName }}
        </i>
      </div>
    </Badge>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import Badge from '@/components/badge/Badge.vue'

const props = defineProps({
  type: {
    type: String,
    default: null
  },
  // NEW PROP for controlling icon library
  source: {
    type: String,
    default: 'fa' // 'fa' or 'material'
  },
  iconStyle: {
    // (kept for backwards compatibility, though you mentioned possibly deprecating it)
    type: String,
    default: ''
  },
  severity: {
    type: String,
    default: ''
  },
  badgeContent: {
    type: String,
    default: ''
  },
  animation: {
    type: String,
    default: null
  },
  faStyle: {
    type: String,
    default: 'fa-duotone fa-regular'
  },
  badgeSize: {
    type: String,
    default: 'md'
  },
  featured: {
    type: Boolean,
    default: false
  }
})

// These two refs will ultimately drive the icon rendering
const iconName = ref(props.type)
const library = ref(props.source)

// A helper function to decide which library to use and what
// the final icon name is. Checks for "fa-" or "material-" prefix.
function updateIcon() {
  if (props.type?.includes('fa-')) {
    library.value = 'fa'
    iconName.value = props.type.replace('fa-', '')
  } else if (props.type?.includes('material-')) {
    library.value = 'material'
    iconName.value = props.type.replace('material-', '')
  } else {
    library.value = props.source // fallback on the source prop
    iconName.value = props.type
  }
}

// Watch both `type` and `source` so changes trigger re-check
watch(
  () => props.type,
  () => {
    updateIcon()
  },
  { immediate: true }
)

watch(
  () => props.source,
  () => {
    updateIcon()
  },
  { immediate: true }
)
</script>

<style>
.icon {
  display: inline-flex;
  position: relative;
}

.material-symbols-outlined {
  font-size: inherit;
  text-align: center;
  width: 1.25em;
}

/* Example: custom icon usage for Font Awesome. */
.fa-magnet-slash:before {
  content: '\f076';
}
.fa-magnet-slash:after {
  position: absolute;
  left: 0;
  top: 0;
  content: '\f715';
  font-weight: 900;
}
</style>
